/**
 * Capture points (Phase 59.6, objective 'points') — Ravenfield / Battlefield conquest. Pure.
 *
 * Points stand on the battle world between the teams. A point flips when one team's living,
 * grounded units stand within its radius and nobody else's do: an owned point is first
 * neutralised, then taken, at a rate of (units on it, up to POINTS_MAX_CAPTURERS) per
 * captureSeconds. Contested points freeze. A team holding fewer points than the leader bleeds
 * respawn tickets; the respawn layer (core/respawn.ts) and the one win pipeline (modes.ts) do
 * the rest — out of tickets with nobody standing is out.
 *
 * Two rules reward fighting *for* a point (lane C, after B4 found that rushing empty points beat
 * every brain that fought; both off when absent, each with a squad-level reading for the surrogate):
 * - **Sight contest** (`contestRange`): an enemy within that many capture radii who can see the
 *   point (terrain and props — the test a bot's eyes use) freezes it. Squad level: an enemy squad
 *   in a cell with sight of the point.
 * - **Capture under fire** (`captureSuppressSeconds`, on by default — decision 117; 0 turns it off): a unit hit by a bullet cannot capture for
 *   that long; it still contests. Squad level: a suppressed cell does not capture.
 *
 * The clock is seconds (the action clock steps it every tick). The turn game does not use this
 * objective yet.
 */

import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { hasLineOfSight, losEyePoint, eyePointAt } from './los';
import { segmentCovered, type BulletEvent } from './bullets';
import type { GameState, CapturePoint, PointsState, SectorFront, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecNormalize, vecLength, vecDot, surfaceTangentFrame } from '../math';
import { dacos, dsin, dcos, datan2 } from './dmath';
import { drainTickets } from './respawn';
import { hqFallBites } from './hqFall';
import { stepMajority, stepTerritory, stepNodes } from './winConditions';
import { battlePlanetIndex } from './actionSetup';
import { personPointOn, personEyePoint, personLineOfSight } from './fineTerrain';
import { outOfBuildings } from './hits';
import {
  SECTOR_DEFAULT_COUNT, SECTOR_DEFAULT_POINTS, SECTOR_START, SECTOR_SPREAD, SECTOR_MAX_SIDE, OPERATIONS_REFILL, OPERATIONS_REAR, TUG_DEFAULT_SECTORS,
  POINTS_DEFAULT_COUNT, POINTS_CAPTURE_SECONDS, POINTS_MAX_CAPTURERS, POINTS_TICKET_BLEED_SECONDS, POINTS_CAPTURE_SUPPRESS_SECONDS, HQ_FALL,
} from '../constants';
import { defaultOn } from './defaultOn';

export type CaptureEvent =
  | { kind: 'neutralised'; pointId: number; byTeam: number; fromTeam: number }
  | { kind: 'captured'; pointId: number; team: number }
  | { kind: 'bled'; team: number; /** 69.5: its HQ has fallen (HQ_FALL) */ hq?: true }
  | { kind: 'sectorTaken'; sector: number; team: number };

function arcOn(a: Vec3, b: Vec3, radius: number): number {
  return dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * radius;
}

/** Each team's centre on the battle world (normalised mean of its units' directions). */
export function teamAnchors(state: Pick<GameState, 'players' | 'planets'>, planetIndex: number): Map<number, Vec3> {
  const sums = new Map<number, Vec3>();
  for (const p of state.players) {
    if (p.planetIndex !== planetIndex) continue;
    const s = sums.get(p.teamIndex) ?? { x: 0, y: 0, z: 0 };
    sums.set(p.teamIndex, vecAdd(s, vecNormalize(p.surfaceDir)));
  }
  const out = new Map<number, Vec3>();
  for (const [t, v] of [...sums].sort((a, b) => a[0] - b[0])) if (vecLength(v) > 1e-9) out.set(t, vecNormalize(v));
  return out;
}

/** Slerp-ish point a fraction `f` of the way from `a` to `b` on the unit sphere, nudged sideways by `side` radians. */
function between(a: Vec3, b: Vec3, f: number, side: number): Vec3 {
  const ang = dacos(Math.max(-1, Math.min(1, vecDot(a, b))));
  let dir: Vec3;
  if (ang < 1e-6) dir = surfaceTangentFrame(a).tx;
  else dir = vecNormalize(vecSub(b, vecScale(a, vecDot(a, b))));
  const p = vecNormalize(vecAdd(vecScale(a, dcos(ang * f)), vecScale(dir, dsin(ang * f))));
  if (side === 0) return p;
  const lateral = vecNormalize({ x: p.y * dir.z - p.z * dir.y, y: p.z * dir.x - p.x * dir.z, z: p.x * dir.y - p.y * dir.x });
  return vecNormalize(vecAdd(vecScale(p, dcos(side)), vecScale(lateral, dsin(side))));
}

/**
 * The points for a match, on the battle world: along the line between the first two teams'
 * landing spots (the front), alternating a little to each side; one team alone → round it.
 */
export function placeCapturePoints(state: GameState): CapturePoint[] {
  if (state.config.modeConfig.pointLayout === 'sectors') return placeSectors(state).sites;
  const count = state.config.modeConfig.pointCount ?? POINTS_DEFAULT_COUNT;
  const bi = battlePlanetIndex(state);
  if (bi < 0 || count <= 0) return [];
  const radius = state.planets[bi].radius;
  const anchors = [...teamAnchors(state, bi).values()];
  const a = anchors[0] ?? { x: 0, y: 1, z: 0 };
  const b = anchors[1] ?? vecNormalize(vecAdd(vecScale(a, -1), surfaceTangentFrame(a).tx));
  const span = arcOn(a, b, radius);
  const sites: CapturePoint[] = [];
  for (let i = 0; i < count; i++) {
    const f = (i + 1) / (count + 1);
    const side = count === 1 ? 0 : ((i % 2 === 0 ? 1 : -1) * Math.min(0.25, span * 0.12 / radius));
    sites.push({ id: i, planetIndex: bi, surfaceDir: between(a, b, f, side), owner: null, capturer: null, progress: 0 });
  }
  return sites;
}

/** The front's frame: `dir` along the great circle at `a`, `n` its normal, `sep` the arc from a to b (radians). */
function frontFrame(front: Pick<SectorFront, 'a' | 'b'>): { dir: Vec3; n: Vec3; sep: number } {
  const { a, b } = front;
  const sep = dacos(Math.max(-1, Math.min(1, vecDot(a, b))));
  const dir = sep < 1e-6 ? surfaceTangentFrame(a).tx : vecNormalize(vecSub(b, vecScale(a, vecDot(a, b))));
  const n = vecNormalize({ x: a.y * dir.z - a.z * dir.y, y: a.z * dir.x - a.x * dir.z, z: a.x * dir.y - a.y * dir.x });
  return { dir, n, sep };
}

/** 69.6d: the place a share `f` of the way along the front, `side` radians off it along the great circle's normal. */
export function frontPoint(front: Pick<SectorFront, 'a' | 'b'>, f: number, side = 0): Vec3 {
  const { dir, n, sep } = frontFrame(front);
  const onLine = vecNormalize(vecAdd(vecScale(front.a, dcos(sep * f)), vecScale(dir, dsin(sep * f))));
  return side === 0 ? onLine : vecNormalize(vecAdd(vecScale(onLine, dcos(side)), vecScale(n, dsin(side))));
}

/** The share of a sector's edges: sector i runs from `edge(i)` to `edge(i + 1)` of the way from a to b. */
export function sectorEdge(front: Pick<SectorFront, 'count' | 'start'>, i: number): number {
  return front.start + (1 - front.start) * i / front.count;
}

/**
 * 69.6b (Foothold's board, decision 146): an Operations push's sectors, in order from team 0's anchor to team 1's along the
 * great circle between them — `sectorCount` sectors of equal depth after team 0's base (`SECTOR_START` of the way), the last
 * reaching team 1's base. Each sector's objectives (`pointsPerSector`, 1–3) stand at its middle, spread across the front
 * (`SECTOR_SPREAD` of the separation apart). Ids run sector by sector. Geometry only, as the line layout is; who attacks and
 * which sector is active are the Operations rule's (69.6d). No bounds: a sector is where its objectives are, not a wall.
 */
export function placeSectors(state: GameState): { sites: CapturePoint[]; front?: SectorFront } {
  const mc = state.config.modeConfig;
  const tug = mc.operations?.shape === 'tug';
  // 183 (4): a layout's pattern gives the count and each sector's objectives (both shipped patterns are odd: a tug's middle)
  const pattern = mc.sectorPattern?.length ? mc.sectorPattern.map(x => Math.max(1, Math.min(4, Math.round(x)))) : null;
  const asked = pattern ? pattern.length : Math.max(1, Math.round(mc.sectorCount ?? (tug ? TUG_DEFAULT_SECTORS : SECTOR_DEFAULT_COUNT)));
  const count = !pattern && tug && asked % 2 === 0 ? asked + 1 : asked;   // 69.6e: the tug of war needs a middle
  const perAll = Math.max(1, Math.min(3, Math.round(mc.pointsPerSector ?? SECTOR_DEFAULT_POINTS)));
  const bi = battlePlanetIndex(state);
  if (bi < 0) return { sites: [] };
  const anchors = [...teamAnchors(state, bi).values()];
  if (mc.operations && (mc.operations.attacker ?? 0) !== 0 && anchors.length >= 2) anchors.reverse();   // 69.6d: from the attacker's side
  const a = anchors[0] ?? { x: 0, y: 1, z: 0 };
  const b = anchors[1] ?? vecNormalize(vecAdd(vecScale(a, -1), surfaceTangentFrame(a).tx));
  const front: SectorFront = { planetIndex: bi, a, b, count, start: SECTOR_START };
  // a lateral offset along the circle's normal keeps its sign at any distance round (between()'s nudge flips past a quarter turn)
  const { dir, n, sep } = frontFrame(front);
  const spread = Math.min(SECTOR_MAX_SIDE, SECTOR_SPREAD * sep);
  const sites: CapturePoint[] = [];
  for (let k = 0; k < count; k++) {
    const f = (sectorEdge(front, k) + sectorEdge(front, k + 1)) / 2;
    const onLine = vecNormalize(vecAdd(vecScale(a, dcos(sep * f)), vecScale(dir, dsin(sep * f))));
    const per = pattern ? pattern[k] : perAll;
    for (let j = 0; j < per; j++) {
      const side = (j - (per - 1) / 2) * spread;
      const surfaceDir = vecNormalize(vecAdd(vecScale(onLine, dcos(side)), vecScale(n, dsin(side))));
      sites.push({ id: sites.length, planetIndex: bi, surfaceDir, owner: null, capturer: null, progress: 0, sector: k });
    }
  }
  return { sites, front };
}

/**
 * 69.6b: which sector a place is in — its share of the way along the front (projected onto the great circle from a to b),
 * read against the sectors' edges. −1 before the first sector (team 0's base, or behind it); past b it is the last sector.
 */
export function sectorAt(front: SectorFront, surfaceDir: Vec3): number {
  const { dir, sep } = frontFrame(front);
  if (sep < 1e-6) return 0;
  const u = vecNormalize(surfaceDir);
  const along = datan2(vecDot(u, dir), vecDot(u, front.a));
  // behind a (the far side of the planet from b) reads as before the first sector
  const f = along / sep;
  if (f < front.start) return -1;
  return Math.min(front.count - 1, Math.floor((f - front.start) / (1 - front.start) * front.count));
}

/** Who stands on a point: living, grounded units of each team within the capture radius. */
export function standingOn(state: GameState, site: CapturePoint): Map<number, number> {
  const r = state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius;
  const radius = state.planets[site.planetIndex]?.radius ?? 1;
  const by = new Map<number, number>();
  for (const p of state.players) {
    if (!p.alive || p.airborne || (p.landingLeft ?? 0) > 0 || p.boarded || p.planetIndex !== site.planetIndex) continue;   // (69.3c: not yet landed; 208: nor aboard)
    if (arcOn(p.surfaceDir, site.surfaceDir, radius) <= r) by.set(p.teamIndex, (by.get(p.teamIndex) ?? 0) + 1);
  }
  return by;
}

/**
 * Sight contest: does a living, grounded enemy of `team` within `range` capture radii see the point
 * (a person-high eye at its centre, terrain then props)? Only asked while a capture would move.
 */
export function watchedByEnemy(state: GameState, site: Pick<CapturePoint, 'planetIndex' | 'surfaceDir'>, team: number, range: number): boolean {   // (69.4 step 4: a relay or FOB too)
  const ps = personSetOf(state);
  const r = (state.config.modeConfig.captureRadius ?? ps.captureRadius) * range;
  const planet = state.planets[site.planetIndex];
  if (!planet) return false;
  let at: Vec3 | null = null;
  for (const p of state.players) {
    if (!p.alive || p.airborne || (p.landingLeft ?? 0) > 0 || p.boarded || p.teamIndex === team || p.planetIndex !== site.planetIndex) continue;
    if (arcOn(p.surfaceDir, site.surfaceDir, planet.radius) > r) continue;
    at ??= personPointOn(planet, site.surfaceDir, ps, ps.losEyeHeight);
    const eye = personEyePoint(p, state.planets, ps, ps.losEyeHeight);
    if (personLineOfSight(eye, at, state) && !segmentCovered(eye, at, state, undefined, ps.coverSampleStep)) return true;
  }
  return false;
}

/** Capture under fire: a bullet hit marks its target suppressed for `seconds` (no-op when the rule is off). */
export function markUnderFire(state: GameState, events: readonly BulletEvent[]): GameState {
  const seconds = state.config.modeConfig.captureSuppressSeconds ?? POINTS_CAPTURE_SUPPRESS_SECONDS;
  if (!seconds || !state.modeState.points) return state;
  const hit = new Set<number>();
  for (const e of events) if (e.kind === 'hitUnit') hit.add(e.targetId);
  if (hit.size === 0) return state;
  return { ...state, players: state.players.map(p => hit.has(p.id) ? { ...p, suppressed: seconds } : p) };
}

/** How many of `team`'s units standing on the point are under fire (suppressed). */
function suppressedOn(state: GameState, site: CapturePoint, team: number): number {
  const r = state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius;
  const radius = state.planets[site.planetIndex]?.radius ?? 1;
  let n = 0;
  for (const p of state.players) {
    if (!p.alive || p.airborne || p.teamIndex !== team || !p.suppressed || p.planetIndex !== site.planetIndex) continue;
    if (arcOn(p.surfaceDir, site.surfaceDir, radius) <= r) n++;
  }
  return n;
}

/** Points each team owns. */
export function pointsHeld(points: PointsState | undefined): Map<number, number> {
  const held = new Map<number, number>();
  for (const s of points?.sites ?? []) if (s.owner !== null) held.set(s.owner, (held.get(s.owner) ?? 0) + 1);
  return held;
}

/** One step of the capture clock (`dt` seconds): flips, then the ticket bleed. */
export function stepCapture(state: GameState, dt: number): { state: GameState; events: CaptureEvent[] } {
  const ps = state.modeState.points;
  if (!ps || ps.sites.length === 0) return { state, events: [] };
  const mc = state.config.modeConfig;
  const seconds = mc.captureSeconds ?? POINTS_CAPTURE_SECONDS;
  const events: CaptureEvent[] = [];
  const suppressing = (mc.captureSuppressSeconds ?? POINTS_CAPTURE_SUPPRESS_SECONDS) > 0 || defaultOn(state.config, 'suppression');   // (65.28: a suppressed unit cannot capture)
  const push = ps.front ? ps.push : undefined;
  const live = push?.shape === 'tug' ? liveSectors(ps) : null;
  const sites = ps.sites.map(site => {
    if (push && (live ? !live.includes(site.sector ?? -1) : site.sector !== push.active)) return site;   // 69.6d: only the active sector (69.6e: the live ones) can be taken
    const on = standingOn(state, site);
    if (on.size !== 1) return site;   // empty or contested: frozen
    const [team, standing] = [...on][0];
    if (site.owner === team && site.progress === 0) return site;   // held, nothing to undo
    if (mc.contestRange && watchedByEnemy(state, site, team, mc.contestRange)) return site;   // seen by an enemy: frozen
    const n = suppressing ? standing - suppressedOn(state, site, team) : standing;
    if (n <= 0) return site;   // everyone on it is under fire: frozen
    const rate = Math.min(n, POINTS_MAX_CAPTURERS) * dt / seconds;
    if (site.owner === team) return { ...site, capturer: null, progress: 0 };   // holding it undoes an enemy's partial flip
    let s: CapturePoint = site.capturer === team ? site : { ...site, capturer: team, progress: 0 };
    const progress = s.progress + rate;
    if (progress < 1) return { ...s, progress };
    if (s.owner !== null) {
      events.push({ kind: 'neutralised', pointId: s.id, byTeam: team, fromTeam: s.owner });
      return { ...s, owner: null, capturer: team, progress: 0 };
    }
    events.push({ kind: 'captured', pointId: s.id, team });
    s = { ...s, owner: team, capturer: null, progress: 0 };
    return s;
  });

  // Bleed: teams holding fewer points than the leader lose a ticket each interval
  let next: GameState = { ...state, modeState: { ...state.modeState, points: { ...ps, sites } } };
  // §0.11 step A (ModeConfig.winConditions): the 'majority' clock, when it is one of the battle's ways to win
  if (mc.winConditions?.use.includes('majority') && !push) next = { ...next, modeState: { ...next.modeState, points: stepMajority(next.modeState.points!, mc.winConditions, dt) } };
  // 216 (4): the territory clock, when it is one of the ways to win (the field memoised on who holds what: a search only when that changes)
  if (mc.winConditions?.use.includes('territory') && !push) next = { ...next, modeState: { ...next.modeState, points: stepTerritory(next, next.modeState.points!, dt) } };
  // Oct 20: the nodes clock (a share of all the objectives held), when it is one of the ways to win
  if (mc.winConditions?.use.includes('nodes') && !push) next = { ...next, modeState: { ...next.modeState, points: stepNodes(next.modeState.points!, mc.winConditions, dt) } };
  // Suppression runs down (only ever set while the rule is on; with 65.28's suppression the Match runs it down, core/suppression.ts)
  if (suppressing && !defaultOn(state.config, 'suppression') && state.players.some(p => p.suppressed)) {
    next = { ...next, players: next.players.map(p => p.suppressed ? { ...p, suppressed: p.suppressed - dt > 0 ? p.suppressed - dt : undefined } : p) };
  }
  const respawn = next.modeState.respawn;
  let bleedClock = ps.bleedClock + dt;
  const interval = mc.ticketBleedSeconds ?? POINTS_TICKET_BLEED_SECONDS;
  if (bleedClock >= interval) {
    bleedClock -= interval;
    const held = pointsHeld({ sites, bleedClock });
    const most = Math.max(0, ...held.values());
    // X19: with reserves on, the tickets are people waiting in the lane — a lost point does not bleed them away
    if (respawn && most > 0 && !state.reserveArrivals && !push && mc.pointBleed !== false) {   // 69.6d: a push spends tickets on arrivals only (216: and a battle with the bleed off)
      let r = respawn;
      const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
      for (const t of teams) {
        if ((held.get(t) ?? 0) < most && (r.tickets[t] ?? 0) > 0) { r = drainTickets(r, t, 1); events.push({ kind: 'bled', team: t }); }
      }
      next = { ...next, modeState: { ...next.modeState, respawn: r } };
    }
    // 69.5 (decision 165, OPEN): a side whose HQ has fallen bleeds HQ_FALL.bleed a second, on this beat
    if (respawn && !push && !state.reserveArrivals && HQ_FALL.bleed > 0) {
      let r = next.modeState.respawn!;
      const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
      for (const t of teams) {
        if ((r.tickets[t] ?? 0) > 0 && hqFallBites(state, t)) { r = drainTickets(r, t, HQ_FALL.bleed * interval); events.push({ kind: 'bled', team: t, hq: true }); }
      }
      next = { ...next, modeState: { ...next.modeState, respawn: r } };
    }
  }
  // 69.5 (165): or the fall ends the battle — its side's tickets all gone at once
  if (HQ_FALL.ends && next.modeState.respawn && !push) {
    let r = next.modeState.respawn;
    for (const t of [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b)) if ((r.tickets[t] ?? 0) > 0 && hqFallBites(state, t)) r = drainTickets(r, t, r.tickets[t] ?? 0);
    next = { ...next, modeState: { ...next.modeState, respawn: r } };
  }
  next = { ...next, modeState: { ...next.modeState, points: { ...next.modeState.points!, sites, bleedClock } } };
  if (push?.shape === 'tug') next = moveBoundary(next, events);
  else if (push) next = advanceFront(next, events);
  return { state: next, events };
}

/**
 * 69.6d: the active sector falls when the attackers hold every objective in it — the front moves to the next sector (its
 * objectives are the defenders' from the start, so the fight moves on), the attackers get their refill, and a
 * `sectorTaken` event says so. Past the last sector `active` is `front.count`: the attackers have won (core/modes.ts).
 */
function advanceFront(state: GameState, events: CaptureEvent[]): GameState {
  const ps = state.modeState.points!;
  const push = ps.push!;
  const inSector = ps.sites.filter(s => s.sector === push.active);
  if (push.active >= ps.front!.count || inSector.length === 0 || !inSector.every(s => s.owner === push.attacker && s.progress === 0)) return state;
  const holder = push.attacker, step = 1;
  events.push({ kind: 'sectorTaken', sector: push.active, team: holder });
  const refill = state.config.modeConfig.operations?.refill ?? OPERATIONS_REFILL;
  const respawn = state.modeState.respawn;
  return {
    ...state,
    modeState: {
      ...state.modeState,
      points: { ...ps, push: { ...push, active: push.active + step } },
      ...(respawn ? { respawn: { ...respawn, tickets: { ...respawn.tickets, [holder]: (respawn.tickets[holder] ?? 0) + refill } } } : {}),
    },
  };
}

/** 69.6d: the objectives in play — under a push, the active sector's only (the bots' and the table's one list); under the
 *  tug of war (69.6e) the live sectors'; else every point. */
export function activeSites(state: Pick<GameState, 'modeState'>): CapturePoint[] {
  const ps = state.modeState.points;
  if (!ps) return [];
  const push = ps.front ? ps.push : undefined;
  if (push?.shape === 'tug') { const live = liveSectors(ps); return ps.sites.filter(s => live.includes(s.sector ?? -1)); }
  return push ? ps.sites.filter(s => s.sector === push.active) : ps.sites;
}

/** A sector's holder: the team that holds every objective in it (none mid-capture), or null. (69.6f's spawn rule reads it under the tug.) */
export function sectorHolder(sites: readonly CapturePoint[], k: number): number | null {
  const own = sites.filter(s => s.sector === k);
  if (own.length === 0) return null;
  const t = own[0].owner;
  return t !== null && own.every(s => s.owner === t && s.progress === 0) ? t : null;
}

/**
 * 69.6e: the tug of war's live sectors — the front is the boundary between the attackers' sectors (from their base) and
 * the defenders' (from theirs). Live are the sectors between the two runs (the neutral middle at the start, a sector part
 * taken); with none between, the attackers' last and the defenders' first. Empty once one side holds every sector.
 */
export function liveSectors(ps: Pick<PointsState, 'sites' | 'front' | 'push'>): number[] {
  const count = ps.front!.count, push = ps.push!;
  let p = 0;
  while (p < count && sectorHolder(ps.sites, p) === push.attacker) p++;
  let q = count;
  while (q > 0 && sectorHolder(ps.sites, q - 1) === push.defender) q--;
  if (p >= count || q <= 0) return [];
  if (p < q) return Array.from({ length: q - p }, (_, i) => p + i);
  return [Math.max(0, q - 1), Math.min(count - 1, p)].filter((k, i, a) => a.indexOf(k) === i);
}

/**
 * 69.6e: after a capture step, any sector that has come wholly into one side's hands since the last step (`push.held`) is taken — a `sectorTaken`
 * event, the refill to the taker — and `active` follows the front: the first live sector, `front.count` once the attackers
 * hold every sector, −1 once the defenders do (core/modes.ts reads the last two as the end).
 */
function moveBoundary(state: GameState, events: CaptureEvent[]): GameState {
  const ps = state.modeState.points!, push = ps.push!, count = ps.front!.count;
  const refill = state.config.modeConfig.operations?.refill ?? OPERATIONS_REFILL;
  let tickets = state.modeState.respawn?.tickets;
  const held = Array.from({ length: count }, (_, k) => sectorHolder(ps.sites, k));
  for (let k = 0; k < count; k++) {
    const now = held[k];
    if (now === null || now === (push.held?.[k] ?? null)) continue;
    events.push({ kind: 'sectorTaken', sector: k, team: now });
    if (tickets) tickets = { ...tickets, [now]: (tickets[now] ?? 0) + refill };
  }
  const live = liveSectors(ps);
  const active = live.length > 0 ? live[0] : sectorHolder(ps.sites, 0) === push.attacker ? count : -1;
  const respawn = state.modeState.respawn;
  return {
    ...state,
    modeState: {
      ...state.modeState,
      points: { ...ps, push: { ...push, active, held } },
      ...(respawn && tickets ? { respawn: { ...respawn, tickets } } : {}),
    },
  };
}

/**
 * 69.6d: set a push up once the sectors are placed — the defenders hold every objective, the first sector is active, and
 * the defenders' arrivals are unlimited. Off (the state as it was) without `operations` or without the sectors' front.
 */
/** 69.6e: the tug of war's start — each side holds the sectors on its half, the middle neutral and the first front. */
function tugStart(ps: PointsState, attacker: number, defender: number): { points: PointsState } {
  const mid = Math.floor(ps.front!.count / 2);
  const owner = (k: number) => (k < mid ? attacker : k > mid ? defender : null);
  const sites = ps.sites.map(s => ({ ...s, owner: owner(s.sector ?? mid) }));
  return { points: { ...ps, sites, push: { attacker, defender, active: mid, shape: 'tug', held: Array.from({ length: ps.front!.count }, (_, k) => sectorHolder(sites, k)) } } };
}

export function startPush(state: GameState): GameState {
  const ps = state.modeState.points;
  const ops = state.config.modeConfig.operations;
  if (!ops || !ps?.front || ps.push) return state;
  const teams = [...teamAnchors(state, ps.front.planetIndex).keys()];
  const attacker = ops.attacker ?? 0;
  const defender = teams.find(t => t !== attacker);
  if (defender === undefined) return state;
  const respawn = state.modeState.respawn;
  const started: GameState = {
    ...state,
    modeState: {
      ...state.modeState,
      ...(ops.shape === 'tug' ? tugStart(ps, attacker, defender) : { points: { ...ps, sites: ps.sites.map(s => ({ ...s, owner: defender })), push: { attacker, defender, active: 0 } } }),
      ...(respawn && ops.shape !== 'tug' ? { respawn: { ...respawn, unlimited: [defender] } } : {}),
    },
  };
  if (ops.shape === 'tug') return started;
  // 69.6c (lane D's look: "it isn't the commander, the defenders aren't at the fight"): a push's defenders start where they
  // would arrive — behind the active sector (respawnSpot's 69.6d branch), not at their base, which on the Foothold board
  // stood 2,450–2,580 people from the first sector (it fell at 195–299 s before they came). Placed in id order, a ring
  // each as a wave lands; their vehicles park behind them after (match/setup.ts)
  let n = 0;
  return {
    ...started,
    players: started.players.map(p => {
      if (p.teamIndex !== defender) return p;
      const spot = respawnSpot(started, defender, undefined, n++);
      return spot ? { ...p, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: spot.heading } : p;
    }),
  };
}


/**
 * Where a team's next unit comes back: beside the owned point nearest its landing spot, else
 * at its landing spot; spread by `k` so a wave does not stack. `atHome` (69.6a, the Foothold board): at `home`, its
 * base — never beside a point, which may stand behind the other side's lines; an Operations push (69.6d) keeps its own rule.
 */
/**
 * 69.6d (the owner, playing: "play should spawn facing objectives"): the way a unit placed at `at` faces — toward the
 * nearest objective of interest to its side: under an Operations push or the tug of war, the nearest objective in play
 * (attackers and defenders alike look at the fight); otherwise the nearest capture point on its planet its side does not
 * hold; else the enemy's base (the Foothold board's, else where the enemy stood at the start, else the enemy's centre).
 * A ground-plane direction at `at` (`PlayerState.heading`), or undefined with nothing to face. One rule for the start,
 * the respawns and every arrival.
 */
export function facingObjective(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }): Vec3 | undefined {
  const here = vecNormalize(at.surfaceDir);
  const pts = state.modeState.points;
  const onPlanet = (s: CapturePoint) => s.planetIndex === at.planetIndex;
  const inPlay = pts?.front && pts.push ? activeSites(state).filter(onPlanet) : [];
  const open = (pts?.sites ?? []).filter(s => onPlanet(s) && s.owner !== team);
  const pool = inPlay.length ? inPlay : open;
  let target: Vec3 | null = null;
  if (pool.length) {
    target = pool.reduce((best, s) => (vecDot(vecNormalize(s.surfaceDir), here) > vecDot(vecNormalize(best.surfaceDir), here) ? s : best)).surfaceDir;
  } else {
    const base = state.board?.bases.find(b => b.team !== team)?.dir;
    const home = Object.entries(state.actionHomes ?? {}).find(([t]) => Number(t) !== team)?.[1];
    const centre = [...teamAnchors(state, at.planetIndex)].find(([t]) => t !== team)?.[1];
    target = base ?? home ?? centre ?? null;
  }
  if (!target) return undefined;
  const t = vecNormalize(target);
  const way = vecSub(t, vecScale(here, vecDot(t, here)));
  return vecLength(way) > 1e-9 ? vecNormalize(way) : undefined;
}

export function respawnSpot(state: GameState, team: number, home: Vec3 | undefined, k: number, atHome = false): { planetIndex: number; surfaceDir: Vec3; heading?: Vec3 } | null {
  const centre = landingCentre(state, team, home, atHome);
  return centre ? ringSpot(state, team, centre, k) : null;
}

/**
 * Where a side's wave lands, before its ring (69.4 step 3 reads it as the push's landing): its nearest owned point, its
 * home, or — under an Operations push or the tug of war — where the push says each side lands. Null with no battle planet.
 */
export function landingCentre(state: GameState, team: number, home: Vec3 | undefined, atHome = false): Vec3 | null {
  const bi = battlePlanetIndex(state);
  if (bi < 0) return null;
  const radius = state.planets[bi].radius;
  const pts = state.modeState.points;
  const push = pts?.front ? pts.push : undefined;
  // 69.6a: on the board, at the base — unless an Operations push (69.6d) says where each side lands
  let owned = atHome && home && !push ? [] : (pts?.sites ?? []).filter(s => s.owner === team && s.planetIndex === bi);
  let centre = home ?? teamAnchors(state, bi).get(team) ?? { x: 0, y: 1, z: 0 };
  if (push?.shape === 'tug' && (team === push.attacker || team === push.defender)) {
    // 69.6e: each side lands behind its own edge of the front — the attackers toward their base, the defenders toward theirs
    const front = pts!.front!;
    const live = liveSectors(pts!);
    const lo = live.length ? live[0] : push.active, hi = live.length ? live[live.length - 1] : push.active;
    // the middle of the sector behind the live span on its side (its own base when the front stands on it), so the ring
    // a wave lands in stays off the front however shallow the sectors are
    const mid = (k: number) => (sectorEdge(front, k) + sectorEdge(front, k + 1)) / 2;
    const f = team === push.attacker ? (lo > 0 ? mid(lo - 1) : 0) : (hi < front.count - 1 ? mid(hi + 1) : 1);
    centre = frontPoint(front, f);
    owned = [];
  } else if (push && team === push.defender) {
    // 69.6d: the defenders land behind the active sector — at its rear edge, a little farther back toward their base
    const front = pts!.front!;
    const active = Math.min(push.active, front.count - 1);
    const depth = (1 - front.start) / front.count;
    const { dir, sep } = frontFrame(front);
    // 69.6c's levers: past the rear edge by a share of a sector (`rear`, today's OPERATIONS_REAR), or a set number of people
    // behind the sector's objectives (its middle, `landPeople`)
    const ops = state.config.modeConfig.operations;
    const f = Math.min(1, ops?.landPeople !== undefined
      ? (sectorEdge(front, active) + sectorEdge(front, active + 1)) / 2 + (ops.landPeople * PERSON_BODY_HEIGHT * personSetOf(state).k) / (Math.max(1e-9, sep) * radius)
      : sectorEdge(front, active + 1) + (ops?.rear ?? OPERATIONS_REAR) * depth);
    centre = vecNormalize(vecAdd(vecScale(front.a, dcos(sep * f)), vecScale(dir, dsin(sep * f))));
    owned = [];
  } else if (push && team === push.attacker) {
    // 69.6d: the attackers come back at the farthest sector they have taken, else at their base
    const far = Math.max(-1, ...owned.map(s => s.sector ?? -1));
    owned = owned.filter(s => s.sector === far);
  }
  if (owned.length > 0) {
    const h = centre;
    centre = owned.reduce((best, s) => arcOn(s.surfaceDir, h, radius) < arcOn(best.surfaceDir, h, radius) ? s : best).surfaceDir;
  }
  return centre;
}

/** The `k`-th spot of a wave's ring round `centre`: just outside a capture circle, spread by person widths, out of buildings, facing the objectives. */
export function ringSpot(state: GameState, team: number, centre: Vec3, k: number): { planetIndex: number; surfaceDir: Vec3; heading?: Vec3 } | null {
  const bi = battlePlanetIndex(state);
  if (bi < 0) return null;
  const radius = state.planets[bi].radius;
  // Offset from the point (not on it: a capture should not be handed over by a respawn) in a ring
  const { tx, ty } = surfaceTangentFrame(centre);
  const a = k * 2.399963;
  // Just outside the capture circle, spread by person widths (lane C: a respawn lands a person, so person scale)
  const ps = personSetOf(state);
  const arc = (ps.captureRadius + (2 + (k % 3)) * ps.k) / radius;
  const along = vecNormalize(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))));
  const spot = vecNormalize(vecAdd(vecScale(centre, dcos(arc)), vecScale(along, dsin(arc))));
  // 63.9: never inside a building (a wave by the base's HQ and pad lands beside them: their walls are closed)
  const surfaceDir = outOfBuildings(state, bi, spot, ps.unitRadius + 0.1 * ps.k);
  const heading = facingObjective(state, team, { planetIndex: bi, surfaceDir });   // 69.6d: facing the objectives
  return { planetIndex: bi, surfaceDir, ...(heading ? { heading } : {}) };
}
